import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AdminAuthProvider, useAdminAuth } from '@/contexts/AdminAuthContext'
import AdminNavbar from '@/components/AdminNavbar'
import AdminRouteGuard from '@/components/AdminRouteGuard'
import { AdminErrorBoundary } from '@/components/AdminErrorBoundary'
import PublicNavbar from '@/components/PublicNavbar'
import AdminSidebar from '@/components/AdminSidebar'
import Breadcrumb from '@/components/Breadcrumb'
import { BreadcrumbProvider } from '@/contexts/BreadcrumbContext'
import { getApiUrl } from '@/lib/api-config'
import AdminDashboard from '@/pages/AdminDashboard'
import AdminLogin from '@/pages/AdminLogin'
import TenantsPage from '@/pages/TenantsPage'
import PlansPage from '@/pages/PlansPage'
import SystemSettingsPage from '@/pages/SystemSettingsPage'
import AuditLogPage from '@/pages/AuditLogPage'
import NotFound from '@/pages/NotFound'
import UserManagementPage from '@/pages/UserManagementPage'
import RolesPage from '@/pages/RolesPage'
import TenantDetailsPage from '@/pages/TenantDetailsPage'
import PlanEditPage from '@/pages/PlanEditPage'
import SubscriptionsPage from '@/pages/SubscriptionsPage'
import AdvancedMonitoringPage from '@/pages/AdvancedMonitoringPage'
import AdminUsersPage from '@/pages/AdminUsersPage'
import AdminRolesPage from '@/pages/AdminRolesPage'
import AdminAccountSettings from '@/pages/AdminAccountSettings'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 5 * 60 * 1000, retry: 1 } }
})

function TitleManager() {
  const location = useLocation()
  const [tenantName, setTenantName] = useState<string | null>(null)
  
  useEffect(() => {
    const pathname = location.pathname || '/'
    const toTitleCase = (value: string) => value.replace(/[/_-]+/g, ' ').trim().replace(/\b\w/g, (char) => char.toUpperCase())
    
    // Check if we're on a tenant-related page
    const tenantMatch = pathname.match(/^\/tenants\/([^/]+)(?:\/.*)?$/)
    const tenantId = tenantMatch ? tenantMatch[1] : null
    
    if (tenantId && tenantId !== 'new') {
      // Fetch tenant name for the title
      const fetchTenantName = async () => {
        try {
          const { auth } = await import('@/lib/firebase')
          const token = await auth.currentUser?.getIdToken()
          if (!token) return
          
          const response = await fetch(getApiUrl(`/admin/tenants/${tenantId}`), {
            headers: { 'Authorization': `Bearer ${token}` }
          })
          
          if (response.ok) {
            const tenant = await response.json()
            setTenantName(tenant.name)
            
            // Determine the page type for the title
            let pageType = 'Details'
            if (pathname.includes('/users')) {
              pageType = 'Users'
            } else if (pathname.includes('/roles')) {
              pageType = 'Roles'
            }
            
            document.title = `Motiv Admin - ${tenant.name} - ${pageType}`
          } else {
            // Fallback to generic title if fetch fails
            document.title = `Motiv Admin - Tenant Details`
          }
        } catch (error) {
          // Fallback to generic title if fetch fails
          document.title = `Motiv Admin - Tenant Details`
        }
      }
      
      fetchTenantName()
    } else {
      // For non-tenant pages, use the existing logic
      const getPageName = (path: string) => { 
        if (path === '/') return 'Dashboard'
        
        // Special handling for plan edit page
        if (path.match(/^\/plans\/[^/]+\/edit$/)) {
          return 'Edit Plan'
        }
        
        const segments = path.split('/').filter(Boolean)
        return segments.length ? toTitleCase(segments[segments.length - 1]) : 'Dashboard' 
      }
      document.title = `Motiv Admin - ${getPageName(pathname)}`
      setTenantName(null)
    }
  }, [location.pathname])
  
  return null
}

function Shell() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const { adminUser, loading } = useAdminAuth()
  const location = useLocation()

  // Early redirect to /login on unsigned root, avoiding blank shell on Pages
  useEffect(() => {
    if (!loading && !adminUser && (location.pathname === '/' || location.pathname === '')) {
      window.history.replaceState(null, '', '/login')
    }
  }, [loading, adminUser, location.pathname])
  
  // Theme is applied early in admin/index.html; avoid re-overriding here
  useEffect(() => { const saved = localStorage.getItem('ui.adminSidebarCollapsed'); if (saved != null) { Promise.resolve().then(() => setIsSidebarCollapsed(saved === 'true')) } }, [])
  const toggleSidebar = () => { setIsSidebarCollapsed((prev) => { const next = !prev; localStorage.setItem('ui.adminSidebarCollapsed', String(next)); return next }) }

  if (loading) return <main style={{padding: 16, fontFamily: 'system-ui, sans-serif'}}>Loading…</main>
  if (!adminUser) {
    // If not authenticated, redirect to login and show PublicNavbar
    if (location.pathname !== '/login') {
      return <Navigate to="/login" replace />
    }
    return (
      <>
        <PublicNavbar />
        <main className="pt-16">
          <Routes>
            <Route path="/login" element={<AdminLogin />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </main>
      </>
    )
  }
  return (
    <BreadcrumbProvider>
      <TitleManager />
      <div className="min-h-screen bg-background">
        <AdminSidebar isCollapsed={isSidebarCollapsed} onToggle={toggleSidebar} />
        <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-16' : 'md:ml-64'}`}>
          <AdminNavbar />
          <main className="pt-1 p-6">
            <div className="mb-4">
              <Breadcrumb />
            </div>
            <Routes>
              <Route path="/" element={<AdminDashboard />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/tenants" element={<AdminRouteGuard permission="tenants:view"><TenantsPage /></AdminRouteGuard>} />
              <Route path="/tenants/:tenantId" element={<AdminRouteGuard permission="tenants:view"><TenantDetailsPage /></AdminRouteGuard>} />
              <Route path="/tenants/:tenantId/users" element={<AdminRouteGuard permission="tenants:users:view"><UserManagementPage /></AdminRouteGuard>} />
              <Route path="/tenants/:tenantId/roles" element={<AdminRouteGuard permission="tenants:roles:view"><RolesPage /></AdminRouteGuard>} />
              <Route path="/plans" element={<AdminRouteGuard permission="plans:view"><PlansPage /></AdminRouteGuard>} />
              <Route path="/plans/:planId/edit" element={<AdminRouteGuard permission="plans:edit"><PlanEditPage /></AdminRouteGuard>} />
              <Route path="/subscriptions" element={<AdminRouteGuard permission="subscriptions:view"><SubscriptionsPage /></AdminRouteGuard>} />
              <Route path="/monitoring" element={<AdminRouteGuard permission="monitoring:view"><AdvancedMonitoringPage /></AdminRouteGuard>} />
              <Route path="/system-settings" element={<AdminRouteGuard permission="settings:view"><SystemSettingsPage /></AdminRouteGuard>} />
              <Route path="/admin-users" element={<AdminRouteGuard permission="admin:users:view"><AdminUsersPage /></AdminRouteGuard>} />
              <Route path="/admin-roles" element={<AdminRouteGuard permission="admin:roles:view"><AdminRolesPage /></AdminRouteGuard>} />
              <Route path="/admin/account-settings" element={<AdminAccountSettings />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
      </div>
    </BreadcrumbProvider>
  )
}

function AdminApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AdminAuthProvider>
          <AdminErrorBoundary>
            <Shell />
          </AdminErrorBoundary>
        </AdminAuthProvider>
      </Router>
    </QueryClientProvider>
  )
}

export default AdminApp
